import React from 'react'
import styled from '@emotion/styled'
import {compiler} from 'markdown-to-jsx'

import {Box} from '@vio/atlas/helpers/Box'
import {TRANSITION_DURATION} from '@vio/atlas/src/components/designSystem/Overlay'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/themes'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {sanitizeHtml} from '@vio/core/src/_web/utils/dom'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {ScrollToBottomButton} from './ScrollToBottomButton'
import {Message, MessageType} from './types'
import {TypingDots} from './TypingDots'
interface Props {
  isFetching: boolean
  messages: Message[]
  scrollRef: React.RefObject<HTMLDivElement>
  scrollToBottom: () => void
  hotelName: string
}

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;

  /* Hide scrollbar for Chrome, Safari, and Edge */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
`

const MessageBox = styled(Box)<{type: MessageType}>`
  padding: ${({type}) =>
    type === MessageType.QUESTION
      ? `${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500}`
      : `${cssTheme.layout.spacing.s000}`};
  background-color: ${({type}) =>
    type === MessageType.QUESTION
      ? `${cssTheme.colors.background.neutral.secondary}`
      : `${cssTheme.colors.background.neutral.primary}`};
  max-width: ${({type}) => (type === MessageType.QUESTION ? '70%' : '100%')};
  margin-bottom: ${cssTheme.layout.spacing.s400};
  word-wrap: break-word;
  white-space: normal;
`

export const HotelQuestionContent: React.FC<Props> = ({
  isFetching,
  hotelName,
  messages,
  scrollRef,
  scrollToBottom
}) => {
  const {ref, inView} = useInView({
    threshold: 0,
    initialInView: true,
    delay: TRANSITION_DURATION
  })
  const {isMobile} = useDeviceLayout()
  return (
    <>
      <ScrollableContent data-body-scroll-lock-ignore ref={scrollRef}>
        <Box padding={isMobile ? 's500' : 's600'} textAlign="start">
          {!messages.length && !isFetching && (
            <Box flex="1">
              <Text variant="titleXL">
                <FormattedMessageWrapper
                  id="chatBotModalHelp"
                  defaultMessage="How can I help?"
                />
              </Text>
              <Box paddingTop="s300">
                <Text variant="bodyM">
                  <FormattedMessageWrapper
                    id="chatBotModalTitle"
                    defaultMessage="You can ask your questions about {hotelName}. AI responses are based on general hotel information."
                    values={{hotelName}}
                  />
                </Text>
              </Box>
            </Box>
          )}

          {messages.map((msg, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent={
                msg.type === MessageType.QUESTION ? 'flex-end' : 'flex-start'
              }
            >
              <MessageBox
                type={msg.type}
                borderRadius="xl"
                backgroundColor={
                  msg.type === MessageType.QUESTION
                    ? 'background.neutral.secondary'
                    : 'background.neutral.primary'
                }
              >
                <Text variant="bodyM">
                  {msg.type === MessageType.QUESTION
                    ? msg.text
                    : compiler(msg.text, {sanitizer: sanitizeHtml})}
                </Text>
              </MessageBox>
            </Box>
          ))}

          {isFetching && (
            <Box display="flex" justifyContent="flex-start">
              <TypingDots />
            </Box>
          )}
          <div ref={ref} />
        </Box>
      </ScrollableContent>
      <ScrollToBottomButton onClick={scrollToBottom} visible={!inView} />
    </>
  )
}
